// @ts-expect-error
import buildMessage from 'ember-changeset-validations/utils/validation-errors';
import { isArray } from '@ember/array';
import application from 'volta/utils/application';
import type { IntlService } from 'ember-intl';

const intl = application?.instance?.lookup('service:intl') as IntlService;

type ListResolver<T> =
  | T[]
  | ((key: string, newValue: T[], oldValue: T[] | undefined, changes: any, content: any) => T[]);

interface ValidateArrayInclusionArgs<T = unknown> {
  list: ListResolver<T>;
  allowBlank?: boolean;
  length?: number;
  i18nKey?: string;
  message?: string | ((key: string, newValue: T[], oldValue: T[] | undefined, changes: any, content: any) => string);
}

function normalizeArray<T>(value: unknown): T[] | null {
  if (!isArray(value)) {
    return null;
  }

  if (Array.isArray(value)) {
    return value as T[];
  }

  const arrayLike = value as { toArray?: () => T[]; slice?: () => T[] };
  if (typeof arrayLike.toArray === 'function') {
    return arrayLike.toArray();
  }
  if (typeof arrayLike.slice === 'function') {
    return arrayLike.slice();
  }

  return Array.from(value as Iterable<T>);
}

export default function validateArrayInclusion<T = unknown>({
  list,
  allowBlank = false,
  length,
  i18nKey,
  message
}: ValidateArrayInclusionArgs<T>) {
  return (key: string, newValue: unknown, oldValue: unknown, changes: any, content: any) => {
    const description = i18nKey ? intl?.t(i18nKey) : key;

    if (newValue === null || newValue === undefined || newValue === '') {
      return (
        allowBlank || buildMessage('inclusion', { type: 'inclusion', context: { description } })
      );
    }

    const values = normalizeArray<T>(newValue);
    if (!values) {
      return buildMessage('collection', { type: 'collection', context: { description } });
    }

    if (values.length === 0) {
      if (allowBlank) {
        return true;
      }
      if (length !== undefined && values.length !== length) {
        return buildMessage('wrongLength', {
          type: 'wrongLength',
          context: { description, is: length }
        });
      }
      return (
        buildMessage('inclusion', { type: 'inclusion', context: { description } })
      );
    }

    if (length !== undefined && values.length !== length) {
      return buildMessage('wrongLength', {
        type: 'wrongLength',
        context: { description, is: length }
      });
    }

    const listValue =
      typeof list === 'function'
        ? list(key, values, oldValue as T[] | undefined, changes, content)
        : list;
    const authorized = normalizeArray<T>(listValue);
    if (!authorized) {
      return buildMessage('invalid', { type: 'invalid', context: { description } });
    }

    const allIncluded = values.every((value) => authorized.includes(value));
    if (allIncluded) {
      return true;
    }

    if (message) {
      return typeof message === 'function'
        ? message(key, values, oldValue as T[] | undefined, changes, content)
        : message;
    }

    return buildMessage('inclusion', { type: 'inclusion', context: { description } });
  };
}
